import React from 'react'
import Cookies from 'js-cookie'
import { remove, capitalize } from 'lodash'
import { Link, Redirect } from 'react-router-dom'

import Snackbar from 'material-ui-legacy/Snackbar'
import {
  PagingState,
  IntegratedPaging,
  SortingState,
  IntegratedSorting,
  SearchState,
  FilteringState,
  IntegratedFiltering,
  EditingState,
  RowDetailState,
  SelectionState
} from '@devexpress/dx-react-grid'
import {
  Grid,
  Toolbar,
  Table,
  TableHeaderRow,
  TableFilterRow,
  TableEditRow,
  TableEditColumn,
  TableRowDetail,
  TableColumnVisibility,
  TableSelection,
  PagingPanel,
  SearchPanel,
  ColumnChooser
} from '@devexpress/dx-react-grid-material-ui'
import Dialog from 'material-ui-legacy/Dialog'
import Panel from 'muicss/lib/react/panel'

import { getWindowHeight } from '../../lib/util.js'
import errors from '../../lib/errors.js'
import LinearLoader from '../UI/LinearLoader'
import CircularLoader from '../UI/CircularLoader'
import BigAddButton from '../UI/BigAddButton'
import ErrorDialog from '../UI/ErrorDialog'
import CloseButton from '../UI/CloseButton'
import PageTitle from '../UI/PageTitle'

import Resource from '../../stores/resource'

// INTERNAL COMPONENTS ---------------------------------------------------------

/*
Props:
- resourcePluralName: Plural name of resource (e.g. "agencies")
- clientRootPath: client URL root path to actions like new & edit (e.g. '/agencies' -> '/agencies/new')
  Used to display the "new" button - hidden when not present
- newUrl: overrides URL created with clientRootPath
- disableNew: Boolean(false) - disables button to create a new entry
- loading: boolean - shows loader when true
*/
const GridHeader = ({
  resourcePluralName,
  loading,
  clientRootPath,
  newUrl,
  disableNew,
  customSubheader
}) => (
  <header>
    <PageTitle>
      {capitalize(resourcePluralName)}
      {loading && (
        <CircularLoader size={32} innerStyle={{ marginLeft: '15px' }} />
      )}
    </PageTitle>
    { customSubheader }
    {!disableNew &&
      (clientRootPath || newUrl) && (
        <Link to={newUrl || `${clientRootPath}/new`}>
          <BigAddButton style={{ float: 'right' }} />
        </Link>
      )}
  </header>
)

/*
MAIN COMPONENT -----------------------------------------------------------------

Props:
- resourceAPIEndpoint: The resource api endpoint (e.g. "agencies")
- resourceSingularName: Singular name of resource (e.g. "agency")
- resourcePluralName: Plural name of resource (e.g. "agencies")
- resourceIncludes: resources that should be included with every resource fetch
                    (e.g.: ['agency', 'client', 'agencyProducer', 'user'] on Project)
- nameAttribute: The attribute (field) used to identify a row (example: name, fullName etc.)
- clientRootPath: client URL root path to actions like new & edit (e.g. '/agencies' -> '/agencies/new')
                  Edit/New actions will be disabled when this isn't present
- newUrl: overrides URL created with clientRootPath (needed for QuoteForm)
- disableNew: Boolean(false) - disables button to create a new entry

- gridColumns: react-grid formatted columns object
- gridRowMappingFn: a mapping function to format row data into react-grid columns

- gridSorting: react-grid formatted sorting
- sortingStateColumnExtensions: react-grid SortingState column extensions
  https://devexpress.github.io/devextreme-reactive/react/grid/docs/guides/sorting/

- gridHiddenColumns: react-grid defaultHiddenColumns
- tableColumnVisibilityExtensions: react-grid TableColumnVisibility extensions
  https://devexpress.github.io/devextreme-reactive/react/grid/docs/guides/column-visibility/

- tableColumnExtensions: react-grid TableColumn extensions
  https://devexpress.github.io/devextreme-reactive/react/grid/docs/guides/data-formatting/
  https://devexpress.github.io/devextreme-reactive/react/grid/docs/reference/table/#tablecolumnextension

- serverFilters: An object containing default filters to be used by Resource
- filters: default client-side filters (see link below)
- filteringStateColumnExtensions: react-grid filtering extensions
  https://devexpress.github.io/devextreme-reactive/react/grid/docs/guides/filtering/

- detailsCard: a *Card element to display item details
- detailsDialogStyle: style object to be used in details modal

- typeProviders: Array of <*TypeProvider> components

- inlineEditing: Boolean, allows inline editing when true
- inlineDeleting: Boolean, allows inline deleting when true
- tableColumnEditingExtensions: react-grid editing extensions, including disabling columns
  https://devexpress.github.io/devextreme-reactive/react/grid/docs/guides/editing/

- rowDetailComponent: RenderProp: used to display a row detail. Row detail plugin will be
                      activated automatically when present.
  https://devexpress.github.io/devextreme-reactive/react/grid/docs/guides/detail-row/

- rowClickFn: function(resourceId) - function override for clicking a row.
              Default action: show detailsCard.
- autoResetSelection: Boolean(false) - automatically resets row selection on click when
                      rowClickFn is present.

- disableHeader: Boolean(false) - disables header with add button
- disablePager: Boolean(false) - disables paging panel
- disableSearch: Boolean(false) - disables global search
- disableFilters: Boolean(false) - disables filtering
- disableVisibility: Boolean(false) - disables column visibility chooser
- customSubheader: JSX.Element - element to render below the title of the grid
*/
class DataGrid extends React.PureComponent {
  constructor(props) {
    super(props)

    const {
      filters,
      gridSorting,
      nameAttribute,
      resourceAPIEndpoint
    } = this.props

    this.resource = Resource(resourceAPIEndpoint)

    this.state = {
      rows: [],
      editingRowIds: [],
      selectedRow: null,
      selection: [],
      filters: filters,
      sorting: gridSorting || [{ columnName: nameAttribute, direction: 'asc' }],
      searchValue: '',
      currentPage: 0,
      detailsLoading: false,
      detailsVisible: false,
      loading: true,
      error: '',
      redirect: null,
      snackbarMessage: ''
    }
  }

  componentWillMount() {
    // Load column setup from cookie
    this._loadGridSetup()
    // Load data
    this._loadData()
  }

  _loadData = () => {
    const { serverFilters, resourceIncludes } = this.props
    this.setState({ loading: true })
    this.resource
      .findAll(serverFilters, resourceIncludes)
      .then(result => {
        this.setState({
          rows: result.data,
          loading: false
        })
      })
      .catch(error => {
        this.setState({ error: errors.unpack(error) })
      })
  }

  _saveGridSetup = () => {
    const { filters, sorting, searchValue } = this.state
    const gridState = {
      filters,
      sorting,
      searchValue
    }
    const cookieName = `${this.props.resourceAPIEndpoint}GridSettings`
    Cookies.set(cookieName, JSON.stringify(gridState), { expires: 1 })
  }

  _loadGridSetup = () => {
    const cookieName = `${this.props.resourceAPIEndpoint}GridSettings`
    const gridState = Cookies.get(cookieName)
    if (!gridState) return

    const { filters, sorting, searchValue } = JSON.parse(gridState)
    this.setState({ filters, sorting, searchValue })
  }

  _getRowByFirstIndex = indexes => this.state.rows[indexes[0]]

  _handleEditingRowIds = editingRowIds => {
    const selectedRow = this._getRowByFirstIndex(editingRowIds)
    if (selectedRow) this.setState({ editingRowIds, selectedRow })
  }

  _commitChanges = ({ added, changed, deleted }) => {
    if (added) {
      // Row added
      console.log('TODO: implement inline add (low priority)')
    }

    if (changed) {
      let rows = this.state.rows
      const { selectedRow, editingRowIds } = this.state
      const resourceId = selectedRow.id
      const rowIndex = editingRowIds[0]
      const changeSet = changed[rowIndex]

      const currentAttributes = selectedRow.attributes
      const updatedAttributes = Object.assign({}, currentAttributes, changeSet)
      const updatedRow = Object.assign({}, selectedRow)
      updatedRow.attributes = updatedAttributes

      this.resource
        .update(resourceId, updatedAttributes)
        .then(result => {
          // Update row stored in state without reload
          rows[rowIndex] = updatedRow
          this.setState({
            rows: rows,
            error: '',
            selectedRow: null,
            snackbarMessage: `${capitalize(this.props.resourceSingularName)} "${
              updatedAttributes[this.props.nameAttribute]
            }" updated!`
          })
        })
        .catch(error =>
          this.setState({ error: errors.unpack(error), selectedRow: null })
        )
    }

    if (deleted) {
      const selectedRow = this._getRowByFirstIndex(deleted)
      this.setState({ selectedRow }, () => this._handleDelete(selectedRow.id))
    }
  }

  _showDetails = id => {
    // Use props.rowClickFn when given, overriding default action
    const { rowClickFn, autoResetSelection, resourceIncludes } = this.props
    if (rowClickFn) {
      rowClickFn(id)
      // NOTE: We're not clearing row selection on purpose here!
      //       The idea is to leave the row selected to create a visual cue
      //       between the row and whatever rowClickFn() is doing.
      //       This can be overridden by setting the prop
      //       autoResetSelection to true:
      if (autoResetSelection) this._clearSelection()
      return
    }

    this.setState({ detailsLoading: true })
    this.resource
      .findBy({ id: id }, resourceIncludes)
      .then(result =>
        this.setState({
          selectedRow: result.data,
          detailsLoading: false,
          detailsVisible: true
        })
      )
      .catch(error => this.setState({ error: errors.unpack(error) }))
  }

  _hideDetails = () =>
    this.setState({ detailsVisible: false, selectedRow: null, selection: [] })

  _handleDelete = id => {
    const { resourceSingularName, nameAttribute } = this.props
    const { rows, selectedRow } = this.state
    this.resource
      .destroy(id)
      .then(result => {
        remove(rows, row => {
          return Number(row.id) === Number(id)
        })
        this.setState({
          detailsVisible: false,
          error: '',
          rows: rows,
          snackbarMessage: `${capitalize(resourceSingularName)} "${
            selectedRow.attributes[nameAttribute]
          }" deleted!`
        })
      })
      .catch(error => this.setState({ error: errors.unpack(error) }))
  }

  _handlePageChange = currentPage =>
    this.setState({ currentPage }, this._saveGridSetup)

  _handleFiltersChange = filters =>
    this.setState({ filters }, this._saveGridSetup)

  _handleSortingChange = sorting =>
    this.setState({ sorting }, this._saveGridSetup)

  _handleSearchChange = searchValue =>
    this.setState({ searchValue }, this._saveGridSetup)

  _handleSelection = selection => {
    // First, handle null selection
    if (selection.length === 0) {
      this.setState({ selectedRow: null })
      return
    }

    // Only handling single selection for now
    const singleSelection = selection[selection.length - 1]

    // Get selectedRow and call this._showDetails
    const selectedRow = this.state.rows[singleSelection]
    this._showDetails(selectedRow.id)

    this.setState({ selection: [singleSelection] })
  }

  _clearSelection = () => this.setState({ selection: [], selectedRow: null })

  _closeErrorDialog = () => this.setState({ error: '' })

  _resetSnackbar = () => this.setState({ snackbarMessage: '' })

  _redirectTo = url => this.setState({ redirect: url })

  render() {
    const {
      tableColumnExtensions,
      tableColumnVisibilityExtensions,
      tableColumnEditingExtensions,
      filteringStateColumnExtensions,
      sortingStateColumnExtensions,
      gridColumns,
      gridRowMappingFn,
      gridHiddenColumns,
      clientRootPath,
      newUrl,
      resourcePluralName,
      disableNew,
      detailsCard,
      detailsDialogStyle,
      typeProviders,
      inlineEditing,
      inlineDeleting,
      rowDetailComponent,
      disableFilters,
      disableSearch,
      disablePager,
      disableHeader,
      disableVisibility,
      customSubheader
    } = this.props

    const {
      redirect,
      loading,
      detailsLoading,
      detailsVisible,
      snackbarMessage,
      error,
      rows,
      selectedRow,
      filters,
      sorting,
      currentPage,
      searchValue,
      selection
    } = this.state

    // Just skip everything if we're redirecting
    if (redirect) return <Redirect push to={redirect} />

    // Same if loading
    if (loading && !disableHeader)
      return (
        <section>
          <GridHeader resourcePluralName={resourcePluralName} loading />
        </section>
      )

    // Get row data based on function received via props
    const gridRows = rows.map(gridRowMappingFn)

    // Calculate page size based on window size
    const perPage = Math.floor((getWindowHeight() - 440) / 48)

    // Make table rows clickable
    const TableRow = ({ row, ...restProps }) => (
      <Table.Row {...restProps} onClick={() => this._showDetails(row.id)} />
    )

    // Just so React doesn't complain about lowercase initials on components
    const Details = detailsCard

    const detailsActions = [<CloseButton onTouchTap={this._hideDetails} />]

    const editRedirect = selectedRow
      ? `${clientRootPath}/${selectedRow.id}/edit`
      : ''

    return (
      <section>
        {!disableHeader && (
          <GridHeader
            resourcePluralName={resourcePluralName}
            clientRootPath={clientRootPath}
            newUrl={newUrl}
            disableNew={disableNew}
            customSubheader={customSubheader}
          />
        )}

        <Panel className="table-grid-panel">
          {gridRows.length === 0 && (
            <h2 className="mui--text-dark-secondary">
              {`No ${resourcePluralName} found.`}
            </h2>
          )}

          {gridRows.length > 0 && (
            <Grid rows={gridRows} columns={gridColumns}>
              {!disableSearch && (
                <SearchState
                  value={searchValue}
                  onValueChange={this._handleSearchChange}
                />
              )}

              {!disableFilters && (
                <FilteringState
                  filters={filters}
                  onFiltersChange={this._handleFiltersChange}
                  columnExtensions={filteringStateColumnExtensions}
                />
              )}
              {!disableFilters && <IntegratedFiltering />}

              <SelectionState
                selection={selection}
                onSelectionChange={this._handleSelection}
              />

              <SortingState
                sorting={sorting}
                onSortingChange={this._handleSortingChange}
                columnExtensions={sortingStateColumnExtensions}
              />
              <IntegratedSorting />

              {!disablePager && (
                <PagingState
                  currentPage={currentPage}
                  onCurrentPageChange={this._handlePageChange}
                  pageSize={perPage}
                />
              )}
              {!disablePager && <IntegratedPaging />}

              {typeProviders}

              {rowDetailComponent && <RowDetailState />}

              {inlineEditing && (
                <EditingState
                  onEditingRowIdsChange={this._handleEditingRowIds}
                  onCommitChanges={this._commitChanges}
                  columnExtensions={tableColumnEditingExtensions}
                />
              )}

              <Table
                rowComponent={TableRow}
                columnExtensions={tableColumnExtensions}
              />

              <TableHeaderRow showSortingControls />

              <TableSelection
                selectByRowClick
                highlightRow
                showSelectionColumn={false}
              />

              {rowDetailComponent && (
                <TableRowDetail contentComponent={rowDetailComponent} />
              )}

              {inlineEditing && <TableEditRow />}
              {inlineEditing && (
                <TableEditColumn
                  showEditCommand
                  showDeleteCommand={inlineDeleting}
                />
              )}

              <Toolbar />

              {!disableSearch && <SearchPanel />}

              {!disableFilters && <TableFilterRow />}

              <TableColumnVisibility
                defaultHiddenColumnNames={gridHiddenColumns}
                columnExtensions={tableColumnVisibilityExtensions}
              />
              {!disableVisibility && <ColumnChooser />}

              {!disablePager && <PagingPanel />}
            </Grid>
          )}
        </Panel>

        {/* Details dialog */}
        {Details && (
          <Dialog
            actions={detailsActions}
            modal={false}
            onRequestClose={this._hideDetails}
            open={detailsLoading || detailsVisible}
            contentStyle={detailsDialogStyle}
          >
            {detailsLoading && <LinearLoader />}
            {!detailsLoading &&
              selectedRow && (
                <Details
                  itemData={selectedRow}
                  editCallback={() => this._redirectTo(editRedirect)}
                  deleteCallback={this.props.cannotDelete ? false : (() => this._handleDelete(selectedRow.id)) }
                />
              )}
          </Dialog>
        )}

        <Snackbar
          open={Boolean(snackbarMessage)}
          message={snackbarMessage}
          autoHideDuration={4000}
          onRequestClose={this._resetSnackbar}
        />

        <ErrorDialog
          open={Boolean(error)}
          closeCallback={this._closeErrorDialog}
        >
          <p>
            <strong>Error:</strong> {error}
          </p>
        </ErrorDialog>
      </section>
    )
  }
}

export default DataGrid



// WEBPACK FOOTER //
// ./src/components/UI/DataGrid.jsx